import React, { FC, useEffect, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { Layout as AntLayout } from "antd";
import { ReactTyped } from "react-typed";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/store";
import { setCurrentProject } from "../store/slices/layout/layoutSlice";
import Head from "./head/Head";
import HeaderMain from "./header/HeaderMain";
import ProjectSidebar from "./sidebar/ProjectSidebar";
import MobileNavigation from "./navigation/MobileNavigation";
import {
  HEADER_HEIGHT,
  PROJECT_SIDEBAR_WIDTH,
  PROJECT_SIDEBAR_COLLAPSED_WIDTH,
} from "./constants";

const { Content } = AntLayout;

interface PaymentAlertDetails {
  overdueCount: number;
  totalDueAmount: number;
  oldestDueDate?: string;
  autoHoldEnabled?: boolean;
  autoHoldDate?: string;
}

const fmtDate = (dt: string) =>
  new Date(dt).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

// Login stashes overdue-invoice details in sessionStorage; the banner below renders
// them. Disabled — flip to true to bring the payment-due banner back.
const SHOW_PAYMENT_DUE_BANNER = false;

const fmtAmt = (v: number) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

interface LayoutProjectProps {
  title?: string;
}

const LayoutProject: FC<LayoutProjectProps> = ({ title }) => {
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();

  const { projectSidebarCollapsed, currentProjectName, currentProjectId } = useSelector(
    (state: RootState) => state.layout
  );

  // Get project info from location state if available
  const locationState = location.state as any;

  // Detail routes where the URL param is NOT projectId (e.g., taskId, issueId, etc.)
  const detailRoutePatterns = ['task-detail', 'issue-detail', 'timelog-detail', 'phase-detail', 'activity-detail'];
  const isDetailRoute = detailRoutePatterns.some(route => location.pathname.includes(route));

  // Extract projectId from URL path or params - only accept numeric segments as projectId
  // URL pattern: /pms/project/dashboard/123 or /pms/project/dashboard
  const pathParts = location.pathname.split('/');
  const projectIdFromPath = pathParts[pathParts.length - 1];

  // For detail routes, don't extract from URL - the last segment is entity ID, not projectId
  // Otherwise require the segment to be numeric so route names like "new-header" aren't treated as ids
  const extractedProjectId = isDetailRoute || !projectIdFromPath || isNaN(Number(projectIdFromPath))
    ? null
    : projectIdFromPath;

  // Validate params.projectId too — react-router can sometimes bind non-numeric segments
  const paramsProjectId = params.projectId && !isNaN(Number(params.projectId)) ? params.projectId : null;
  const stateProjectId = locationState?.id && !isNaN(Number(locationState.id))
    ? locationState.id
    : (locationState?.projectId && !isNaN(Number(locationState.projectId)) ? locationState.projectId : null);

  // Priority: validated params > validated path extract > validated state > Redux fallback (last known project)
  const projectId = paramsProjectId || extractedProjectId || stateProjectId || currentProjectId;
  const projectName = locationState?.projectName || currentProjectName || "Project";

  useEffect(() => {
    // Only update Redux when we resolved a *new* valid projectId from URL/state.
    // Falling back to currentProjectId is fine but should not re-dispatch (already set).
    const freshProjectId = paramsProjectId || extractedProjectId || stateProjectId;

    if (freshProjectId && freshProjectId !== currentProjectId) {
      dispatch(
        setCurrentProject({
          id: freshProjectId,
          name: locationState?.projectName || currentProjectName || null,
          status: locationState?.derivedProjectStatus,
        })
      );
    } else if (freshProjectId && locationState?.projectName && locationState.projectName !== currentProjectName) {
      // Keep project name in sync when only the name changes
      dispatch(
        setCurrentProject({
          id: freshProjectId,
          name: locationState.projectName,
          status: locationState?.derivedProjectStatus,
        })
      );
    }
  }, [paramsProjectId, extractedProjectId, stateProjectId, locationState?.projectName, currentProjectId, currentProjectName, dispatch]);

  const sidebarWidth = projectSidebarCollapsed
    ? PROJECT_SIDEBAR_COLLAPSED_WIDTH
    : PROJECT_SIDEBAR_WIDTH;

  const [paymentAlert, setPaymentAlert] = useState<PaymentAlertDetails | null>(
    null
  );
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  useEffect(() => {
    const alertData = sessionStorage.getItem("pending_payment_alert");
    if (!SHOW_PAYMENT_DUE_BANNER) {
      // Still drain the key so a stale alert can't resurface later in the session.
      sessionStorage.removeItem("pending_payment_alert");
      return;
    }
    if (alertData) {
      sessionStorage.removeItem("pending_payment_alert");
      try {
        const parsed = JSON.parse(alertData);
        const d: PaymentAlertDetails = parsed.details || {};
        setPaymentAlert(d);
        setIsAlertOpen(true);
      } catch {}
    }
  }, []);

  const buildSingleString = (d: PaymentAlertDetails): string => {
    const parts: string[] = [
      `<strong>Pending Invoices:</strong> ${d.overdueCount || 0}`,
      `<strong>Total Due:</strong> <span style="color:#c0392b;font-weight:700">₹ ${fmtAmt(
        d.totalDueAmount
      )}</span>`,
    ];
    if (d.oldestDueDate) {
      parts.push(
        `<strong>Overdue Since:</strong> ${fmtDate(d.oldestDueDate)}`
      );
    }
    if (
      d.autoHoldEnabled &&
      d.autoHoldDate &&
      new Date(d.autoHoldDate) > new Date()
    ) {
      parts.push(
        `<strong style="color:#c0392b">Account Hold:</strong> <span style="color:#c0392b">${fmtDate(
          d.autoHoldDate
        )}</span>`
      );
    }
    parts.push(
      `<span style="color:#856404">Please contact your administrator.</span>`
    );
    return parts.join("&nbsp;&nbsp;|&nbsp;&nbsp;");
  };

  const handleDismiss = () => setIsAlertOpen(false);

  return (
    <>
      <Head title={title || projectName || "Project"} />

      {paymentAlert && isAlertOpen && (
        <>
          {/* Blur/dim overlay — click anywhere to dismiss */}
          <div
            onClick={handleDismiss}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1058,
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              backgroundColor: "rgba(0, 0, 0, 0.35)",
              cursor: "pointer",
            }}
          />

          {/* Payment-due banner — pinned above sticky header */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1059,
              background:
                "linear-gradient(90deg, #fff3cd 0%, #ffe08a 50%, #fff3cd 100%)",
              borderBottom: "2px solid #f0ad4e",
              boxShadow: "0 3px 14px rgba(0,0,0,0.22)",
              padding: "10px 48px 10px 16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              minHeight: "42px",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#e67e22",
                color: "#fff",
                borderRadius: "6px",
                padding: "3px 10px",
                fontWeight: 700,
                fontSize: "12px",
                letterSpacing: "0.5px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              ⚠ PAYMENT DUE
            </span>
            <span style={{ fontSize: "13px", color: "#533f03", flex: 1 }}>
              <ReactTyped
                strings={[buildSingleString(paymentAlert)]}
                typeSpeed={18}
                showCursor={false}
                loop={false}
              />
            </span>
            <button
              onClick={handleDismiss}
              aria-label="Close"
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "18px",
                color: "#856404",
                lineHeight: 1,
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              ×
            </button>
          </div>
        </>
      )}

      <AntLayout
        style={{
          height: "100vh",
          overflow: "hidden",
          background: "#f5f6fa",
        }}
      >
        {/* Fixed Header with Main Navigation */}
        <HeaderMain fixed showNavigation />

        {/* Mobile Navigation Drawer */}
        <MobileNavigation />

        {/* Project Sidebar */}
        <ProjectSidebar projectId={projectId} projectName={projectName} />

        {/* Main Content Area - the single scroll container */}
        <Content
          className="project-content-pane pms-main-content"
          style={{
            marginTop: HEADER_HEIGHT,
            marginLeft: sidebarWidth,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            padding: "0 24px 24px",
            overflow: "auto",
            transition: "margin-left 0.3s ease-in-out",
          }}
        >
          <Outlet />
        </Content>
      </AntLayout>

      {/* Responsive styles for mobile */}
      <style>{`
        @media (max-width: 992px) {
          .project-content-pane {
            margin-left: 0 !important;
          }
        }
      `}</style>
    </>
  );
};

export default LayoutProject;
